import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Check } from "lucide-react";
import api from "../../api/axios";

const INTERESTS = [
  { value: "pro_matches", label: "Score restricted competitions (IPL / ICC events / India domestic)" },
  { value: "archive",      label: "Access to historical match archive (3,200+ matches)" },
  { value: "white_label",  label: "White-labelled scorecards / data feeds" },
  { value: "custom_integration", label: "Custom integration / data API for our platform" },
];

export default function ContactSales() {
  const [form, setForm] = useState({
    name: "", email: "", org_name: "", role: "",
    league_or_association: "", phone: "", message: "",
    interested_in: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInterest = (v) => setForm((f) => ({
    ...f,
    interested_in: f.interested_in.includes(v)
      ? f.interested_in.filter((x) => x !== v)
      : [...f.interested_in, v],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/api/scoring/sales/inquiry/", {
        ...form,
        interested_in: form.interested_in.join(","),
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center p-4">
        <div className="bg-bg-primary border border-emerald-500/30 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-text-primary mb-2">Thanks — we'll be in touch.</h2>
          <p className="text-text-secondary text-sm mb-5">
            Our admin or someone from the team will email you within 24 hours.
            For urgent queries, reply directly to that thread.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-emerald-400 hover:underline text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main py-6 sm:py-12 px-3 sm:px-4">
      <div className="max-w-xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-text-secondary hover:text-text-primary text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
          Talk to our team
        </h1>
        <p className="text-text-secondary text-sm mb-6">
          State associations, league owners, franchises, ICC Associate Nations — let us know what you need and we'll set up a tailored plan. Fill this in and someone will email within 24 hours.
        </p>

        <form onSubmit={submit} className="bg-bg-primary border border-border-default rounded-xl p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Your name" required>
              <input value={form.name} onChange={(e) => update("name", e.target.value)} required
                className="input" maxLength={200} />
            </Field>
            <Field label="Work email" required>
              <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required
                className="input" maxLength={254} />
            </Field>
          </div>

          <Field label="Organisation / association name" required>
            <input value={form.org_name} onChange={(e) => update("org_name", e.target.value)} required
              className="input" maxLength={200}
              placeholder="e.g. Mumbai Cricket Association, Royal Challengers Bangalore" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Your role">
              <input value={form.role} onChange={(e) => update("role", e.target.value)}
                className="input" maxLength={100}
                placeholder="e.g. Operations Director" />
            </Field>
            <Field label="Phone (optional)">
              <input value={form.phone} onChange={(e) => update("phone", e.target.value)}
                className="input" maxLength={50} placeholder="+91 ..." />
            </Field>
          </div>

          <Field label="League / tournament you're focused on">
            <input value={form.league_or_association}
              onChange={(e) => update("league_or_association", e.target.value)}
              className="input" maxLength={200}
              placeholder="e.g. Ranji Trophy, IPL, Mumbai Premier League" />
          </Field>

          <Field label="What are you interested in?">
            <div className="space-y-1.5 mt-1">
              {INTERESTS.map((it) => (
                <label key={it.value} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.interested_in.includes(it.value)}
                    onChange={() => toggleInterest(it.value)}
                    className="mt-1 accent-button-primary"
                  />
                  <span className="text-text-secondary">{it.label}</span>
                </label>
              ))}
            </div>
          </Field>

          <Field label="Anything else">
            <textarea value={form.message} onChange={(e) => update("message", e.target.value)}
              className="input min-h-[80px] resize-y" maxLength={2000}
              placeholder="Tournament dates, scale, integration needs, anything specific to your situation." />
          </Field>

          {error && (
            <div className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded p-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !form.name || !form.email || !form.org_name}
            className="w-full flex items-center justify-center gap-2 bg-button-primary text-white px-4 py-2.5 rounded-lg font-semibold hover:brightness-110 disabled:opacity-50 transition-all"
          >
            <Send className="w-4 h-4" />
            {submitting ? "Sending…" : "Send to sales"}
          </button>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          background: var(--color-bg-secondary, #1f2937);
          border: 1px solid var(--color-border-default, #374151);
          border-radius: 0.375rem;
          color: var(--color-text-primary, #fff);
          font-size: 0.875rem;
          outline: none;
        }
        .input:focus { border-color: var(--color-button-primary, #6b1991); }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1">
        {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
